import { PRMarinaRect } from '@sketch-hq/sketch-web-renderer'
import { useMemo } from 'react'
import {
  HorizontalMeasurement,
  InspectMeasurements,
  Line,
  VerticalMeasurement,
} from './types'
import { SketchElement } from '../../../../inspector'

/**
 * Get the coordinate and sizes of the lines to draw between the two provided elements.
 * We can draw up to 4 lines, two for each axis.
 * The size of each line is the distance in Sketch pixels, so we can use it to display
 * the distance value. This also means that we need to convert it to CSS pixels
 * before drawing the lines on top of the canvas.
 */
export function useMeasurementData(
  selectedElement: SketchElement | null,
  hoveredElement: SketchElement | null
) {
  const measurementData = useMemo((): InspectMeasurements | null => {
    const hoveredMarinaNode = hoveredElement?.prMarinaNode
    if (!selectedElement || !hoveredMarinaNode) {
      return null
    }

    const hoveredElementBounds = hoveredMarinaNode.getAbsoluteLayerBounds()
    const selectedElementBounds =
      selectedElement.prMarinaNode.getAbsoluteLayerBounds()

    const horizontalMeasurements = getHorizontalMeasurement(
      selectedElementBounds,
      hoveredElementBounds
    )

    const verticalMeasurements = getVerticalMeasurement(
      selectedElementBounds,
      hoveredElementBounds
    )

    return {
      ...horizontalMeasurements,
      ...verticalMeasurements,
    }
  }, [hoveredElement?.prMarinaNode, selectedElement])

  return measurementData
}

// MARK: HORIZONTAL

/**
 * Get measurements on the horizontal axis only.
 */
function getHorizontalMeasurement(
  selectedBounds: PRMarinaRect,
  hoveredBounds: PRMarinaRect
): HorizontalMeasurement {
  const intersectionMeasurement = getIntersectionHorizontalMeasurement(
    selectedBounds,
    hoveredBounds
  )
  if (intersectionMeasurement) {
    return intersectionMeasurement
  }

  return getOutsideHorizontalMeasurement(selectedBounds, hoveredBounds)
}

/**
 * If there is an intersection between the two elements on the horizontal axis,
 * we return the measurements for the intersection.
 * If there is no intersection, we return null.
 * In this scenario we measure between matching sides (left of bounds 1 with left of bounds 2, etc... ).
 */
function getIntersectionHorizontalMeasurement(
  selectedBounds: PRMarinaRect,
  hoveredBounds: PRMarinaRect
): HorizontalMeasurement | null {
  const intersectionStartX = Math.max(selectedBounds.x, hoveredBounds.x)
  const intersectionEndX = Math.min(
    selectedBounds.x + selectedBounds.width,
    hoveredBounds.x + hoveredBounds.width
  )
  if (intersectionEndX <= intersectionStartX) {
    // No horizontal intersection
    return null
  }

  const y = selectedBounds.y + selectedBounds.height / 2

  const leftFromX = selectedBounds.x
  const leftToX = hoveredBounds.x

  const left: Line = {
    x: Math.min(leftFromX, leftToX),
    y,
    size: Math.abs(leftToX - leftFromX),
  }
  const leftProjection = makeVerticalProjection(
    { x: leftToX, y: left.y },
    hoveredBounds,
    selectedBounds
  )

  const rightFromX = selectedBounds.x + selectedBounds.width
  const rightToX = hoveredBounds.x + hoveredBounds.width
  const right: Line = {
    x: Math.min(rightFromX, rightToX),
    y,
    size: Math.abs(rightToX - rightFromX),
  }
  const rightProjection = makeVerticalProjection(
    { x: rightToX, y: right.y },
    hoveredBounds,
    selectedBounds
  )

  return {
    left,
    leftProjection,
    right,
    rightProjection,
  }
}

/**
 * Get measurements on the horizontal axis only when there is no intersection between
 * the two elements on the horizontal axis.
 * In this scenario we measure between opposite sides (right of bounds 1 with left of bounds 2, etc... ).
 */
function getOutsideHorizontalMeasurement(
  selectedBounds: PRMarinaRect,
  hoveredBounds: PRMarinaRect
): HorizontalMeasurement {
  const y = selectedBounds.y + selectedBounds.height / 2
  if (selectedBounds.x < hoveredBounds.x) {
    // Selected is to the left of hovered, the line
    // is starting from the right side of the selected element

    const fromX = selectedBounds.x + selectedBounds.width
    const toX = hoveredBounds.x
    const right: Line = {
      x: Math.min(fromX, toX),
      y,
      size: Math.abs(toX - fromX),
    }

    const rightProjection = makeVerticalProjection(
      { x: right.x + right.size, y: right.y },
      hoveredBounds,
      selectedBounds
    )

    return { right, rightProjection }
  }

  // Selected is to the right of hovered
  // Line starting from the left side of the selected element
  const fromX = selectedBounds.x
  const toX = hoveredBounds.x + hoveredBounds.width
  const left: Line = {
    x: Math.min(fromX, toX),
    y,
    size: Math.abs(toX - fromX),
  }

  const leftProjection = makeVerticalProjection(
    { x: left.x, y: left.y },
    hoveredBounds,
    selectedBounds
  )

  return { left, leftProjection }
}

/**
 * Calculate the horizontal projection line (red dashes),
 * extending from the hovered bounds and passing through the given point.
 */
function makeHorizontalProjection(
  point: { x: number; y: number },
  hoveredBounds: PRMarinaRect,
  selectedBounds: PRMarinaRect
): Line | undefined {
  if (
    point.x >= hoveredBounds.x &&
    point.x <= hoveredBounds.x + hoveredBounds.width
  ) {
    // No projection needed, the point is on the hovered bounds
    return undefined
  }

  if (point.x < hoveredBounds.x) {
    // Hovered bounds is to the right of the point
    return {
      x: selectedBounds.x,
      y: point.y,
      size: hoveredBounds.x - selectedBounds.x,
    }
  }

  // Hovered bounds is to the left of the point

  return {
    x: hoveredBounds.x + hoveredBounds.width,
    y: point.y,
    size:
      selectedBounds.x +
      selectedBounds.width -
      hoveredBounds.x -
      hoveredBounds.width,
  }
}

// MARK: VERTICAL

/**
 * Get measurements on the vertical axis only.
 */
function getVerticalMeasurement(
  selectedBounds: PRMarinaRect,
  hoveredBounds: PRMarinaRect
): VerticalMeasurement {
  const intersectionMeasurement = getIntersectionVerticalMeasurement(
    selectedBounds,
    hoveredBounds
  )
  if (intersectionMeasurement) {
    return intersectionMeasurement
  }

  return getOutsideVerticalMeasurement(selectedBounds, hoveredBounds)
}

/**
 * If there is an intersection between the two elements on the vertical axis,
 * we return the measurements for the intersection.
 * If there is no intersection, we return null.
 * In this scenario we measure between matching sides (top of bounds 1 with top of bounds 2, etc... ).
 */
function getIntersectionVerticalMeasurement(
  selectedBounds: PRMarinaRect,
  hoveredBounds: PRMarinaRect
): VerticalMeasurement | null {
  const intersectionStartY = Math.max(selectedBounds.y, hoveredBounds.y)
  const intersectionEndY = Math.min(
    selectedBounds.y + selectedBounds.height,
    hoveredBounds.y + hoveredBounds.height
  )
  if (intersectionEndY <= intersectionStartY) {
    // No vertical intersection
    return null
  }

  const x = selectedBounds.x + selectedBounds.width / 2

  const topFromY = selectedBounds.y
  const topToY = hoveredBounds.y

  const top: Line = {
    x,
    y: Math.min(topFromY, topToY),
    size: Math.abs(topToY - topFromY),
  }

  const topProjection = makeHorizontalProjection(
    { x: top.x, y: topToY },
    hoveredBounds,
    selectedBounds
  )

  const bottomFromY = selectedBounds.y + selectedBounds.height
  const bottomToY = hoveredBounds.y + hoveredBounds.height
  const bottom: Line = {
    x,
    y: Math.min(bottomFromY, bottomToY),
    size: Math.abs(bottomToY - bottomFromY),
  }

  const bottomProjection = makeHorizontalProjection(
    { x: bottom.x, y: bottomToY },
    hoveredBounds,
    selectedBounds
  )

  return {
    top,
    topProjection,
    bottom,
    bottomProjection,
  }
}

/**
 * Get measurements on the vertical axis only when there is no intersection between
 * the two elements on the vertical axis.
 * In this scenario we measure between opposite sides (bottom of bounds 1 with top of bounds 2, etc...).
 */
function getOutsideVerticalMeasurement(
  selectedBounds: PRMarinaRect,
  hoveredBounds: PRMarinaRect
): VerticalMeasurement {
  const x = selectedBounds.x + selectedBounds.width / 2

  if (selectedBounds.y < hoveredBounds.y) {
    // Selected is above hovered, the line
    // is starting from the bottom side of the selected element

    const fromY = selectedBounds.y + selectedBounds.height
    const toY = hoveredBounds.y
    const bottom: Line = {
      x,
      y: Math.min(fromY, toY),
      size: Math.abs(toY - fromY),
    }

    const bottomProjection = makeHorizontalProjection(
      { x: bottom.x, y: toY },
      hoveredBounds,
      selectedBounds
    )

    return { bottom, bottomProjection }
  }

  // Selected is below hovered
  // Line starting from the top side of the selected element
  const fromY = selectedBounds.y
  const toY = hoveredBounds.y + hoveredBounds.height
  const top: Line = {
    x,
    y: Math.min(fromY, toY),
    size: Math.abs(toY - fromY),
  }

  const topProjection = makeHorizontalProjection(
    { x: top.x, y: toY },
    hoveredBounds,
    selectedBounds
  )

  return { top, topProjection }
}

/**
 * Calculate the vertical projection line (red dashes),
 * extending from the hovered bounds and passing through the given point.
 */
function makeVerticalProjection(
  point: { x: number; y: number },
  hoveredBounds: PRMarinaRect,
  selectedBounds: PRMarinaRect
): Line | undefined {
  if (
    point.y >= hoveredBounds.y &&
    point.y <= hoveredBounds.y + hoveredBounds.height
  ) {
    // No projection needed, the point is on the hovered bounds
    return undefined
  }

  if (point.y < hoveredBounds.y) {
    // Hovered bounds is below the point
    return {
      x: point.x,
      y: selectedBounds.y,
      size: hoveredBounds.y - selectedBounds.y,
    }
  }

  // Hovered bounds is above the point
  return {
    x: point.x,
    y: hoveredBounds.y + hoveredBounds.height,
    size:
      selectedBounds.y +
      selectedBounds.height -
      hoveredBounds.y -
      hoveredBounds.height,
  }
}
